import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Select } from 'antd';

import { DocumentReference } from '@totem/components/documents/types';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SALES_ORDERS_ENDPOINT } from '@totem/utilities/endpoints';

type LinkTarget = 'organization' | 'building' | 'salesOrder';

// Every sales order line for the org in one request; the picker lists each sales order once.
const SALES_ORDER_PAGE_SIZE = 5000;

type SalesOrderOption = {
  value: string;
  label: string;
  reference: DocumentReference;
};

type Props = {
  // complete is false while a building or sales order still has to be picked.
  onChange: (reference: DocumentReference | null, complete: boolean) => void;
};

// "Link to" for documents added from the Document Repository page, where there is no record to attach to.
// A building link matches the documents the building page adds; a sales order link matches contract sync's,
// so the document also shows on that sales order.
const LinkToSelector = ({ onChange }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  // Already limited to the regions and buildings the user's data access allows.
  const { regions } = useContext(OrganizationContext);
  const [target, setTarget] = useState<LinkTarget>('organization');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [salesOrders, setSalesOrders] = useState<SalesOrderOption[] | null>(
    null,
  );
  const [loadingSalesOrders, setLoadingSalesOrders] = useState<boolean>(false);

  // Linking to a sales order is for Intelligent Buildings users only.
  const canLinkSalesOrders = permissions.internal.hasPermissions(userProfile);

  const buildingGroups = useMemo(
    () =>
      [...(regions ?? [])]
        .filter((region) => (region.children ?? []).length > 0)
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((region) => ({
          label: region.name,
          options: [...(region.children ?? [])]
            .sort((left, right) => left.name.localeCompare(right.name))
            .map((building) => ({
              value: building.id,
              label: building.name,
              reference: {
                regionId: region.id,
                buildingId: building.id,
                objectReference: {
                  type: 'Building',
                  id: building.id,
                  name: building.name,
                },
              } as DocumentReference,
            })),
        })),
    [regions],
  );

  useEffect(() => {
    if (target !== 'salesOrder' || salesOrders !== null) {
      return;
    }
    setLoadingSalesOrders(true);

    fetch(SALES_ORDERS_ENDPOINT, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify({
        paging: { page: 1, pageSize: SALES_ORDER_PAGE_SIZE },
        sort: { field: 'nsSalesOrder.tranId', direction: -1 },
        filters: [],
      }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: { lines: SalesOrderLine[] }) => {
        const byNumber = new Map<string, SalesOrderOption>();
        (result.lines ?? []).forEach((line) => {
          const { tranId, id } = line.nsSalesOrder;
          if (!tranId || byNumber.has(tranId)) {
            return;
          }
          byNumber.set(tranId, {
            value: tranId,
            label: line.building?.name
              ? `${tranId} · ${line.building.name}`
              : tranId,
            reference: {
              regionId: line.region?.id,
              buildingId: line.building?.id,
              objectReference: {
                type: 'Sales Order',
                id,
                name: tranId,
              },
            } as DocumentReference,
          });
        });

        setSalesOrders(
          [...byNumber.values()].sort((left, right) =>
            right.value.localeCompare(left.value),
          ),
        );
      })
      .catch(() => setSalesOrders([]))
      .finally(() => setLoadingSalesOrders(false));
  }, [target, salesOrders, getApiToken]);

  const choose = (nextTarget: LinkTarget) => {
    setTarget(nextTarget);

    setSelectedId(null);

    onChange(null, nextTarget === 'organization');
  };

  const pick = (id: string, reference: DocumentReference) => {
    setSelectedId(id);

    onChange(reference, true);
  };

  const targetOptions = [
    { value: 'organization', label: 'Organization only' },
    { value: 'building', label: 'Building' },
    ...(canLinkSalesOrders
      ? [{ value: 'salesOrder', label: 'Sales order' }]
      : []),
  ];

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item label="Link to">
          <Select
            value={target}
            options={targetOptions}
            onChange={(value: LinkTarget) => choose(value)}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        {target === 'building' && (
          <Form.Item label="Building" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select a building"
              value={selectedId}
              options={buildingGroups}
              onChange={(value: string) =>
                pick(
                  value,
                  buildingGroups
                    .flatMap((group) => group.options)
                    .find((option) => option.value === value)?.reference,
                )
              }
            />
          </Form.Item>
        )}
        {target === 'salesOrder' && (
          <Form.Item label="Sales order" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Search by order number or building"
              loading={loadingSalesOrders}
              value={selectedId}
              options={salesOrders ?? []}
              notFoundContent={
                loadingSalesOrders ? 'Loading…' : 'No sales orders found'
              }
              onChange={(value: string, option) =>
                pick(value, (option as SalesOrderOption).reference)
              }
            />
          </Form.Item>
        )}
      </Col>
    </Row>
  );
};

export default LinkToSelector;
